import { useReturnNavigation } from "../../../app/navigation/return-navigation.js";
import { Button, Card, IconTile } from "@peer-start/ui";
import { ArrowRight, BrainCircuit, RotateCcw, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function AreaAssessmentConversation({ area, pageId }: { area: string; pageId: string }) {
  const { t } = useTranslation();
  const back = useReturnNavigation(`/me/passport/${pageId}`);
  const [answers, setAnswers] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [nextStep, setNextStep] = useState("discuss");
  const latest = useRef<HTMLDivElement>(null);
  const complete = answers.length === 3;
  const questions = ["experience", "example", "direction"] as const;
  const suggestions = [
    ["studied", "worked", "little"],
    ["independent", "supported", "unsure"],
    ["learn", "practice", "evidence"],
  ];
  useEffect(() => {
    if (answers.length) latest.current?.scrollIntoView({ block: "nearest" });
  }, [answers.length]);
  const send = (text: string, choice?: string) => {
    if (!text.trim() || complete) return;
    if (answers.length === 2) setNextStep(choice ?? "discuss");
    setAnswers((current) => [...current, text.trim()]);
    setDraft("");
  };
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border bg-muted/20 px-5 py-4">
        <IconTile>
          <BrainCircuit aria-hidden="true" className="size-6" />
        </IconTile>
        <div>
          <h2 className="font-medium">{t("areaAssessment.botName")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t(complete ? "areaAssessment.complete" : "areaAssessment.progress", {
              current: Math.min(answers.length + 1, 3),
              total: 3,
            })}
          </p>
        </div>
      </div>
      <div
        className="space-y-5 p-5 sm:p-6"
        role="log"
        aria-label={t("areaAssessment.conversation")}
        aria-live="polite"
        aria-relevant="additions text"
      >
        {questions.slice(0, Math.min(answers.length + 1, 3)).map((question, index) => (
          <div className="space-y-3" key={question}>
            <div className="mr-5 rounded-2xl rounded-tl-sm bg-muted/40 p-4 text-sm leading-relaxed sm:mr-12">
              <p className="sr-only">{t("areaAssessment.botName")}</p>
              {index === 0 ? <p className="mb-3">{t("areaAssessment.intro", { area })}</p> : null}
              <p>{t(`areaAssessment.questions.${question}`)}</p>
            </div>
            {answers[index] ? (
              <div className="ml-5 whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-primary p-4 text-sm leading-relaxed text-primary-foreground sm:ml-12">
                <p className="sr-only">{t("areaAssessment.you")}</p>
                {answers[index]}
              </div>
            ) : null}
          </div>
        ))}
        <div ref={latest}>
          {complete ? (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
              <h3 className="font-medium">{t("areaAssessment.resultTitle")}</h3>
              <p className="mt-2 text-sm leading-relaxed">
                {t(`areaAssessment.recommendations.${nextStep}`)}
              </p>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                {t("areaAssessment.resultNote")}
              </p>
              <Link
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                data-control-id="area-assessment.return"
                to={back.to}
              >
                {t(back.labelKey)}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </div>
          ) : null}
        </div>
      </div>
      {!complete ? (
        <form
          className="border-t border-border p-5 sm:p-6"
          onSubmit={(event) => {
            event.preventDefault();
            send(draft);
          }}
        >
          <p className="mb-3 text-xs text-muted-foreground">{t("areaAssessment.chooseOrWrite")}</p>
          <div className="mb-4 flex flex-wrap gap-2">
            {suggestions[answers.length]!.map((choice) => (
              <button
                className="rounded-full border border-primary/20 bg-primary/5 px-3 py-2 text-left text-xs font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                data-control-id={`area-assessment.suggestion.${choice}`}
                key={choice}
                onClick={() => send(t(`areaAssessment.suggestions.${choice}`), choice)}
                type="button"
              >
                {t(`areaAssessment.suggestions.${choice}`)}
              </button>
            ))}
          </div>
          <label className="sr-only" htmlFor="area-assessment-answer">
            {t("areaAssessment.answer")}
          </label>
          <textarea
            className="min-h-24 w-full resize-y rounded-xl border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="area-assessment.answer"
            id="area-assessment-answer"
            maxLength={2000}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("areaAssessment.placeholder")}
            rows={3}
            value={draft}
          />
          <div className="mt-3 flex justify-end">
            <Button controlId="area-assessment.send" disabled={!draft.trim()} type="submit">
              {t("areaAssessment.send")}
              <Send aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </form>
      ) : (
        <div className="border-t border-border px-5 py-3">
          <Button
            controlId="area-assessment.restart"
            variant="ghost"
            onClick={() => {
              setAnswers([]);
              setDraft("");
              setNextStep("discuss");
            }}
          >
            <RotateCcw aria-hidden="true" className="size-4" />
            {t("areaAssessment.restart")}
          </Button>
        </div>
      )}
      <p className="border-t border-border bg-muted/20 px-5 py-3 text-xs leading-relaxed text-muted-foreground">
        {t("areaAssessment.demoNotice")}
      </p>
    </Card>
  );
}
